import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem,
  Snackbar, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Delete, Edit, Link as LinkIcon, PhoneAndroid, PhoneIphone, Save, SystemUpdate } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import { bulkUpsertSettings, getSettings } from '../../services/platform/settingsService';
import {
  createMobileAppVersion,
  deleteMobileAppVersion,
  listMobileAppVersions,
  updateMobileAppVersion,
  type MobileAppKey,
  type MobileAppVersionPayload,
  type MobileAppVersionRow,
  type MobilePlatform,
} from '../../services/mobileAppVersionService';

interface PlatformGateForm {
  minVersion: string;
  latestVersion: string;
  storeUrl: string;
}

interface AppVersionForm {
  maintenance: boolean;
  maintenanceMessage: string;
  forceMessage: string;
  softMessage: string;
  android: PlatformGateForm;
  ios: PlatformGateForm;
}

const APPS: { key: MobileAppKey; label: string; hint: string }[] = [
  { key: 'user', label: 'Customer App', hint: 'SuprAppUser / end customers' },
  { key: 'partner', label: 'Partner App', hint: 'Drivers & delivery partners' },
  { key: 'provider', label: 'Provider App', hint: 'On-demand service providers' },
];

const APP_LABEL: Record<MobileAppKey, string> = {
  user: 'Customer',
  partner: 'Partner',
  vendor: 'Vendor',
  provider: 'Provider',
};

const CATEGORY = 'mobile_versioning';

const DEFAULT_STORE: Record<MobileAppKey, { android: string; ios: string }> = {
  user: {
    android: 'https://play.google.com/store/apps/details?id=com.supersaas.super_saas_mobile',
    ios: 'https://apps.apple.com/in/app/taxiapps-proplus-user/id6800609891',
  },
  partner: {
    android: 'https://play.google.com/store/apps/details?id=com.supersaas.partner_app',
    ios: 'https://apps.apple.com/in/app/taxiapps-proplus-partner/id6801026856',
  },
  vendor: {
    android: '',
    ios: '',
  },
  provider: {
    android: 'https://play.google.com/store/apps/details?id=com.supersaas.provider_app',
    ios: 'https://apps.apple.com/in/app/taxiapps-proplus-provider/id6801138889',
  },
};

const DEFAULTS: Record<MobileAppKey, AppVersionForm> = {
  user: {
    maintenance: false,
    maintenanceMessage: 'We are under maintenance. Please try again shortly.',
    forceMessage: 'A new version is required. Please update to continue.',
    softMessage: 'A newer version is available. Update for the best experience.',
    android: {
      minVersion: '0.0.0',
      latestVersion: '1.0.0',
      storeUrl: DEFAULT_STORE.user.android,
    },
    ios: {
      minVersion: '0.0.0',
      latestVersion: '1.0.0',
      storeUrl: DEFAULT_STORE.user.ios,
    },
  },
  partner: {
    maintenance: false,
    maintenanceMessage: 'We are under maintenance. Please try again shortly.',
    forceMessage: 'A new version is required. Please update to continue.',
    softMessage: 'A newer version is available. Update for the best experience.',
    android: {
      minVersion: '0.0.0',
      latestVersion: '1.0.0',
      storeUrl: DEFAULT_STORE.partner.android,
    },
    ios: {
      minVersion: '0.0.0',
      latestVersion: '1.0.0',
      storeUrl: DEFAULT_STORE.partner.ios,
    },
  },
  vendor: {
    maintenance: false,
    maintenanceMessage: '',
    forceMessage: '',
    softMessage: '',
    android: { minVersion: '0.0.0', latestVersion: '0.0.0', storeUrl: '' },
    ios: { minVersion: '0.0.0', latestVersion: '0.0.0', storeUrl: '' },
  },
  provider: {
    maintenance: false,
    maintenanceMessage: 'We are under maintenance. Please try again shortly.',
    forceMessage: 'A new version is required. Please update to continue.',
    softMessage: 'A newer version is available. Update for the best experience.',
    android: {
      minVersion: '0.0.0',
      latestVersion: '1.0.0',
      storeUrl: DEFAULT_STORE.provider.android,
    },
    ios: {
      minVersion: '0.0.0',
      latestVersion: '1.0.0',
      storeUrl: DEFAULT_STORE.provider.ios,
    },
  },
};

function clonePlatformGate(g: PlatformGateForm): PlatformGateForm {
  return { ...g };
}

function cloneAppForm(f: AppVersionForm): AppVersionForm {
  return {
    ...f,
    android: clonePlatformGate(f.android),
    ios: clonePlatformGate(f.ios),
  };
}

function emptyForms(): Record<MobileAppKey, AppVersionForm> {
  return {
    user: cloneAppForm(DEFAULTS.user),
    partner: cloneAppForm(DEFAULTS.partner),
    vendor: cloneAppForm(DEFAULTS.vendor),
    provider: cloneAppForm(DEFAULTS.provider),
  };
}

function readGateVersion(
  map: Map<string, string>,
  app: MobileAppKey,
  platform: 'android' | 'ios',
  kind: 'min' | 'latest',
  fallback: string,
): string {
  const platformKey = `${app}_${platform}_${kind}_version`;
  const legacyKey = `${app}_${kind}_version`;
  const androidKey = `${app}_android_${kind}_version`;
  const iosKey = `${app}_ios_${kind}_version`;

  const direct = map.get(platformKey);
  if (direct) return direct;

  const hasAndroid = Boolean(map.get(androidKey));
  const hasIos = Boolean(map.get(iosKey));
  const legacy = map.get(legacyKey);

  // Legacy keys apply to both platforms only before any platform-specific value exists.
  if (legacy && !hasAndroid && !hasIos) return legacy;

  return fallback;
}

function mapFromSettings(rows: { key: string; value: string }[]): Record<MobileAppKey, AppVersionForm> {
  const map = new Map(rows.map((r) => [r.key, r.value ?? '']));
  const next = emptyForms();
  (Object.keys(next) as MobileAppKey[]).forEach((app) => {
    const d = DEFAULTS[app];
    next[app] = {
      maintenance: (map.get(`${app}_maintenance`) ?? String(d.maintenance)) === 'true',
      maintenanceMessage: map.get(`${app}_maintenance_message`) || d.maintenanceMessage,
      forceMessage: map.get(`${app}_force_message`) || d.forceMessage,
      softMessage: map.get(`${app}_soft_message`) || d.softMessage,
      android: {
        minVersion: readGateVersion(map, app, 'android', 'min', d.android.minVersion),
        latestVersion: readGateVersion(map, app, 'android', 'latest', d.android.latestVersion),
        storeUrl: map.get(`${app}_android_url`) || d.android.storeUrl,
      },
      ios: {
        minVersion: readGateVersion(map, app, 'ios', 'min', d.ios.minVersion),
        latestVersion: readGateVersion(map, app, 'ios', 'latest', d.ios.latestVersion),
        storeUrl: map.get(`${app}_ios_url`) || d.ios.storeUrl,
      },
    };
  });
  return next;
}

function toSettingsPayload(forms: Record<MobileAppKey, AppVersionForm>) {
  const settings: { category: string; key: string; value: string }[] = [];
  (Object.keys(forms) as MobileAppKey[]).forEach((app) => {
    const f = forms[app];
    settings.push(
      { category: CATEGORY, key: `${app}_maintenance`, value: f.maintenance ? 'true' : 'false' },
      { category: CATEGORY, key: `${app}_maintenance_message`, value: f.maintenanceMessage.trim() },
      { category: CATEGORY, key: `${app}_android_min_version`, value: f.android.minVersion.trim() || '0.0.0' },
      { category: CATEGORY, key: `${app}_android_latest_version`, value: f.android.latestVersion.trim() || '0.0.0' },
      { category: CATEGORY, key: `${app}_ios_min_version`, value: f.ios.minVersion.trim() || '0.0.0' },
      { category: CATEGORY, key: `${app}_ios_latest_version`, value: f.ios.latestVersion.trim() || '0.0.0' },
      { category: CATEGORY, key: `${app}_force_message`, value: f.forceMessage.trim() },
      { category: CATEGORY, key: `${app}_soft_message`, value: f.softMessage.trim() },
      { category: CATEGORY, key: `${app}_android_url`, value: f.android.storeUrl.trim() },
      { category: CATEGORY, key: `${app}_ios_url`, value: f.ios.storeUrl.trim() },
    );
  });
  return settings;
}

function isSemverLike(v: string) {
  return /^\d+(\.\d+){0,3}$/.test(v.trim());
}

type ReleaseForm = MobileAppVersionPayload & { uploadedAt?: string };

function cloneReleaseForm(f: ReleaseForm): ReleaseForm {
  return { ...f };
}

function releaseDraftKey(app: MobileAppKey, platform: MobilePlatform) {
  return `${app}:${platform}`;
}

function blankRelease(app: MobileAppKey, platform: MobilePlatform = 'android'): ReleaseForm {
  return {
    app,
    platform,
    version: '',
    buildNumber: '',
    storeUrl: DEFAULT_STORE[app][platform],
    releaseNotes: '',
    uploadedAt: new Date().toISOString().slice(0, 10),
    isActive: true,
    isLatest: false,
    isMinRequired: false,
  };
}

function applyReleaseToGateForms(
  forms: Record<MobileAppKey, AppVersionForm>,
  release: ReleaseForm,
): Record<MobileAppKey, AppVersionForm> {
  const next = { ...forms, [release.app]: cloneAppForm(forms[release.app]) };
  const platform = release.platform === 'ios' ? 'ios' : 'android';
  const gate = next[release.app][platform];
  if (release.isLatest) gate.latestVersion = release.version.trim();
  if (release.isMinRequired) gate.minVersion = release.version.trim();
  if (release.storeUrl.trim()) gate.storeUrl = release.storeUrl.trim();
  return next;
}

export default function AppVersioningPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [platformTab, setPlatformTab] = useState<MobilePlatform>('android');
  const [forms, setForms] = useState(emptyForms);
  const [flash, setFlash] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [releaseForm, setReleaseForm] = useState<ReleaseForm>(blankRelease('user'));
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [tableAppFilter, setTableAppFilter] = useState<'all' | MobileAppKey>('all');
  const [tablePlatformFilter, setTablePlatformFilter] = useState<'all' | MobilePlatform>('all');
  const releaseDraftsRef = useRef<Record<string, ReleaseForm>>({});

  const { data: rows = [], isLoading, isError } = useQuery({
    queryKey: ['settings', CATEGORY],
    queryFn: () => getSettings(CATEGORY),
  });

  const {
    data: versionRows = [],
    isLoading: versionsLoading,
    isError: versionsError,
  } = useQuery({
    queryKey: ['mobile-app-versions'],
    queryFn: () => listMobileAppVersions(),
  });

  useEffect(() => {
    if (!rows.length) return;
    setForms(mapFromSettings(rows));
  }, [rows]);

  const switchReleasePlatform = (platform: MobilePlatform) => {
    if (editingId) return;
    setReleaseForm((prev) => {
      releaseDraftsRef.current[releaseDraftKey(prev.app, prev.platform)] = cloneReleaseForm(prev);
      const cached = releaseDraftsRef.current[releaseDraftKey(prev.app, platform)];
      return cached ? { ...cached, platform } : blankRelease(prev.app, platform);
    });
  };

  const switchReleaseApp = (app: MobileAppKey) => {
    setReleaseForm((prev) => {
      releaseDraftsRef.current[releaseDraftKey(prev.app, prev.platform)] = cloneReleaseForm(prev);
      const cached = releaseDraftsRef.current[releaseDraftKey(app, prev.platform)];
      return cached ? { ...cached, app } : blankRelease(app, prev.platform);
    });
  };

  const closeReleaseDialog = () => {
    setReleaseOpen(false);
    setEditingId(null);
    releaseDraftsRef.current = {};
  };

  const saveMut = useMutation({
    mutationFn: () => bulkUpsertSettings(toSettingsPayload(forms)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings', CATEGORY] });
      setFlash({ severity: 'success', message: 'App versioning settings saved.' });
    },
    onError: (err: unknown) => {
      setFlash({ severity: 'error', message: err instanceof Error ? err.message : 'Failed to save settings' });
    },
  });

  const saveReleaseMut = useMutation({
    mutationFn: async () => {
      const payload: MobileAppVersionPayload = {
        ...releaseForm,
        version: releaseForm.version.trim(),
        storeUrl: releaseForm.storeUrl.trim(),
        buildNumber: (releaseForm.buildNumber || '').trim(),
        releaseNotes: (releaseForm.releaseNotes || '').trim(),
        uploadedAt: releaseForm.uploadedAt
          ? new Date(releaseForm.uploadedAt).toISOString()
          : undefined,
      };
      if (editingId) return updateMobileAppVersion(editingId, payload);
      return createMobileAppVersion(payload);
    },
    onSuccess: async () => {
      const wasEdit = Boolean(editingId);
      setForms((prev) => applyReleaseToGateForms(prev, releaseForm));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mobile-app-versions'] }),
        queryClient.invalidateQueries({ queryKey: ['settings', CATEGORY] }),
      ]);
      closeReleaseDialog();
      setFlash({
        severity: 'success',
        message: wasEdit ? 'Store version updated.' : 'Store version added.',
      });
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      setFlash({
        severity: 'error',
        message: ax.response?.data?.message || ax.message || 'Failed to save store version',
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteMobileAppVersion(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mobile-app-versions'] });
      setDeleteId(null);
      setFlash({ severity: 'success', message: 'Store version deleted.' });
    },
    onError: (err: unknown) => {
      setFlash({ severity: 'error', message: err instanceof Error ? err.message : 'Delete failed' });
    },
  });

  const toggleActiveMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateMobileAppVersion(id, { isActive }),
    onMutate: ({ id }) => setTogglingId(id),
    onSuccess: async (updated) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mobile-app-versions'] }),
        queryClient.invalidateQueries({ queryKey: ['settings', CATEGORY] }),
      ]);
      setFlash({
        severity: 'success',
        message: updated.isActive ? 'Store version enabled.' : 'Store version disabled.',
      });
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      setFlash({
        severity: 'error',
        message: ax.response?.data?.message || ax.message || 'Failed to update status',
      });
    },
    onSettled: () => setTogglingId(null),
  });

  const active = APPS[tab];
  const form = forms[active.key];
  const platformForm = platformTab === 'ios' ? form.ios : form.android;

  const validationError = useMemo(() => {
    for (const app of Object.keys(forms) as MobileAppKey[]) {
      const f = forms[app];
      for (const platform of ['android', 'ios'] as const) {
        const p = f[platform];
        if (!isSemverLike(p.minVersion)) {
          return `${APP_LABEL[app]} ${platform === 'android' ? 'Android' : 'iOS'} min version must look like 1.0.0`;
        }
        if (!isSemverLike(p.latestVersion)) {
          return `${APP_LABEL[app]} ${platform === 'android' ? 'Android' : 'iOS'} latest version must look like 1.0.0`;
        }
      }
    }
    return '';
  }, [forms]);

  const patch = (partial: Partial<AppVersionForm>) => {
    setForms((prev) => ({
      ...prev,
      [active.key]: { ...cloneAppForm(prev[active.key]), ...partial },
    }));
  };

  const patchPlatform = (partial: Partial<PlatformGateForm>) => {
    const platform = platformTab === 'ios' ? 'ios' : 'android';
    setForms((prev) => ({
      ...prev,
      [active.key]: {
        ...cloneAppForm(prev[active.key]),
        [platform]: { ...prev[active.key][platform], ...partial },
      },
    }));
  };

  const filteredVersions = useMemo(() => {
    return versionRows.filter((r) => {
      if (tableAppFilter !== 'all' && r.app !== tableAppFilter) return false;
      if (tablePlatformFilter !== 'all' && r.platform !== tablePlatformFilter) return false;
      return true;
    });
  }, [versionRows, tableAppFilter, tablePlatformFilter]);

  const openCreate = () => {
    setEditingId(null);
    releaseDraftsRef.current = {};
    const platform = platformTab === 'ios' ? 'ios' : 'android';
    setReleaseForm(blankRelease(active.key, platform));
    setReleaseOpen(true);
  };

  const openEdit = (row: MobileAppVersionRow) => {
    setEditingId(row._id);
    releaseDraftsRef.current = {};
    setReleaseForm({
      app: row.app,
      platform: row.platform,
      version: row.version,
      buildNumber: row.buildNumber || '',
      storeUrl: row.storeUrl,
      releaseNotes: row.releaseNotes || '',
      uploadedAt: row.uploadedAt ? String(row.uploadedAt).slice(0, 10) : '',
      isActive: row.isActive,
      isLatest: row.isLatest,
      isMinRequired: row.isMinRequired,
    });
    setReleaseOpen(true);
  };

  const columns: GridColDef<MobileAppVersionRow>[] = useMemo(() => [
    {
      field: 'app',
      headerName: 'App',
      width: 120,
      valueGetter: (_v, row) => APP_LABEL[row.app] || row.app,
    },
    {
      field: 'platform',
      headerName: 'Store',
      width: 110,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.platform === 'android' ? 'Play Store' : 'App Store'}
          color={row.platform === 'android' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    { field: 'version', headerName: 'Version', width: 110 },
    { field: 'buildNumber', headerName: 'Build', width: 90, valueGetter: (_v, row) => row.buildNumber || '—' },
    {
      field: 'storeUrl',
      headerName: 'Store URL',
      flex: 1,
      minWidth: 220,
      renderCell: ({ row }) => (
        <Typography
          component="a"
          href={row.storeUrl}
          target="_blank"
          rel="noreferrer"
          variant="body2"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {row.storeUrl}
        </Typography>
      ),
    },
    {
      field: 'uploadedAt',
      headerName: 'Uploaded',
      width: 120,
      valueGetter: (_v, row) => (row.uploadedAt ? String(row.uploadedAt).slice(0, 10) : '—'),
    },
    {
      field: 'flags',
      headerName: 'Flags',
      width: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {row.isLatest ? <Chip size="small" color="primary" label="Latest" /> : null}
          {row.isMinRequired ? <Chip size="small" color="warning" label="Min / Force" /> : null}
        </Stack>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Switch
          size="small"
          checked={!!row.isActive}
          disabled={togglingId === row._id}
          onChange={(e) => toggleActiveMut.mutate({ id: row._id, isActive: e.target.checked })}
          inputProps={{ 'aria-label': row.isActive ? 'Disable store version' : 'Enable store version' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" aria-label="Edit" onClick={() => openEdit(row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Delete" color="error" onClick={() => setDeleteId(row._id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ], [togglingId]);

  return (
    <PageShell>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SystemUpdate color="primary" /> App Versioning
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Configure maintenance / force / soft update per app and per store (Play Store vs App Store),
            then manage uploaded builds in the table below.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          disabled={saveMut.isPending || !!validationError || isLoading}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? 'Saving…' : 'Save gate settings'}
        </Button>
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Could not load versioning settings.</Alert>}
      {validationError && <Alert severity="warning" sx={{ mb: 2 }}>{validationError}</Alert>}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          {APPS.map((a) => (
            <Tab
              key={a.key}
              icon={<PhoneAndroid fontSize="small" />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {a.label}
                  {forms[a.key].maintenance ? <Chip size="small" color="warning" label="Maintenance" /> : null}
                </Box>
              }
            />
          ))}
        </Tabs>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700}>{active.label}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{active.hint}</Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.maintenance}
                    onChange={(e) => patch({ maintenance: e.target.checked })}
                  />
                }
                label="Maintenance mode (blocks app on splash)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Maintenance message"
                value={form.maintenanceMessage}
                onChange={(e) => patch({ maintenanceMessage: e.target.value })}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Tabs
                value={platformTab}
                onChange={(_, v) => setPlatformTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
              >
                <Tab
                  icon={<PhoneAndroid fontSize="small" />}
                  iconPosition="start"
                  label="Android / Play Store"
                  value="android"
                />
                <Tab
                  icon={<PhoneIphone fontSize="small" />}
                  iconPosition="start"
                  label="iOS / App Store"
                  value="ios"
                />
              </Tabs>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                key={`${active.key}-${platformTab}-min`}
                fullWidth
                size="small"
                label={`${platformTab === 'android' ? 'Android' : 'iOS'} min version (force)`}
                helperText="Overridden when a table row is marked Min / Force"
                value={platformForm.minVersion}
                onChange={(e) => patchPlatform({ minVersion: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                key={`${active.key}-${platformTab}-latest`}
                fullWidth
                size="small"
                label={`${platformTab === 'android' ? 'Android' : 'iOS'} latest version (soft)`}
                helperText="Overridden when a table row is marked Latest"
                value={platformForm.latestVersion}
                onChange={(e) => patchPlatform({ latestVersion: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                key={`${active.key}-${platformTab}-store`}
                fullWidth
                size="small"
                label={platformTab === 'android' ? 'Play Store URL' : 'App Store URL'}
                value={platformForm.storeUrl}
                onChange={(e) => patchPlatform({ storeUrl: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Force update message"
                value={form.forceMessage}
                onChange={(e) => patch({ forceMessage: e.target.value })}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Soft update message"
                value={form.softMessage}
                onChange={(e) => patch({ softMessage: e.target.value })}
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Uploaded store versions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Record each Play Store / App Store build. Mark <strong>Latest</strong> for soft update or{' '}
                <strong>Min / Force</strong> for mandatory update. Use the status switch to enable or disable a build.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add version
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              select
              size="small"
              label="App"
              value={tableAppFilter}
              onChange={(e) => setTableAppFilter(e.target.value as 'all' | MobileAppKey)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All apps</MenuItem>
              {APPS.map((a) => (
                <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Store"
              value={tablePlatformFilter}
              onChange={(e) => setTablePlatformFilter(e.target.value as 'all' | MobilePlatform)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All stores</MenuItem>
              <MenuItem value="android">Play Store</MenuItem>
              <MenuItem value="ios">App Store</MenuItem>
            </TextField>
          </Stack>

          {versionsError && <Alert severity="error" sx={{ mb: 1 }}>Could not load store versions.</Alert>}

          <SerialNoDataGrid
            rows={filteredVersions}
            columns={columns}
            getRowId={(r) => r._id}
            loading={versionsLoading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: 0 }}
          />
        </CardContent>
      </Card>

      <Dialog
        open={releaseOpen}
        onClose={closeReleaseDialog}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            >
              <SystemUpdate fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {editingId ? 'Edit store version' : 'Add store version'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Register a Play Store or App Store build and control soft / force update flags.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.1 }}>
                Target app
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {APPS.map((a) => {
                  const selected = releaseForm.app === a.key;
                  return (
                    <Chip
                      key={a.key}
                      label={a.label}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => switchReleaseApp(a.key)}
                      sx={{ fontWeight: selected ? 700 : 500 }}
                    />
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.1 }}>
                Store platform
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                disabled={Boolean(editingId)}
                value={releaseForm.platform}
                onChange={(e) => switchReleasePlatform(e.target.value as MobilePlatform)}
                sx={{ mt: 1 }}
                SelectProps={{
                  renderValue: (value) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {value === 'ios' ? <PhoneIphone fontSize="small" /> : <PhoneAndroid fontSize="small" />}
                      <span>{value === 'ios' ? 'App Store · iOS' : 'Play Store · Android'}</span>
                    </Stack>
                  ),
                }}
              >
                <MenuItem value="android">Play Store · Android</MenuItem>
                <MenuItem value="ios">App Store · iOS</MenuItem>
              </TextField>
              {editingId ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                  Store platform cannot be changed while editing an existing release.
                </Typography>
              ) : null}
            </Box>

            <Card variant="outlined" sx={{ bgcolor: (t) => alpha(t.palette.grey[500], 0.04) }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Build details — {releaseForm.platform === 'ios' ? 'App Store · iOS' : 'Play Store · Android'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      key={`${releaseForm.app}-${releaseForm.platform}-version`}
                      fullWidth
                      size="small"
                      label="Version"
                      placeholder="1.0.2"
                      value={releaseForm.version}
                      onChange={(e) => setReleaseForm((p) => ({ ...p, version: e.target.value }))}
                      error={!!releaseForm.version && !isSemverLike(releaseForm.version)}
                      helperText="Semver e.g. 1.0.2"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      key={`${releaseForm.app}-${releaseForm.platform}-build`}
                      fullWidth
                      size="small"
                      label="Build number"
                      placeholder="102"
                      value={releaseForm.buildNumber || ''}
                      onChange={(e) => setReleaseForm((p) => ({ ...p, buildNumber: e.target.value }))}
                      helperText="Optional native build"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      key={`${releaseForm.app}-${releaseForm.platform}-date`}
                      fullWidth
                      size="small"
                      type="date"
                      label="Published date"
                      InputLabelProps={{ shrink: true }}
                      value={releaseForm.uploadedAt || ''}
                      onChange={(e) => setReleaseForm((p) => ({ ...p, uploadedAt: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      key={`${releaseForm.app}-${releaseForm.platform}-url`}
                      fullWidth
                      size="small"
                      label="Store URL"
                      placeholder={releaseForm.platform === 'ios' ? 'https://apps.apple.com/...' : 'https://play.google.com/...'}
                      value={releaseForm.storeUrl}
                      onChange={(e) => setReleaseForm((p) => ({ ...p, storeUrl: e.target.value }))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      key={`${releaseForm.app}-${releaseForm.platform}-notes`}
                      fullWidth
                      size="small"
                      label="Release notes"
                      placeholder="Bug fixes and performance improvements"
                      multiline
                      minRows={3}
                      value={releaseForm.releaseNotes || ''}
                      onChange={(e) => setReleaseForm((p) => ({ ...p, releaseNotes: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Update policy
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderColor: releaseForm.isActive ? 'primary.main' : 'divider',
                      bgcolor: releaseForm.isActive
                        ? (t) => alpha(t.palette.primary.main, 0.04)
                        : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Active</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Include in version checks
                          </Typography>
                        </Box>
                        <Switch
                          checked={!!releaseForm.isActive}
                          onChange={(e) => setReleaseForm((p) => ({ ...p, isActive: e.target.checked }))}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderColor: releaseForm.isLatest ? 'info.main' : 'divider',
                      bgcolor: releaseForm.isLatest
                        ? (t) => alpha(t.palette.info.main, 0.06)
                        : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Latest</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Soft update target
                          </Typography>
                        </Box>
                        <Switch
                          checked={!!releaseForm.isLatest}
                          onChange={(e) => setReleaseForm((p) => ({ ...p, isLatest: e.target.checked }))}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderColor: releaseForm.isMinRequired ? 'warning.main' : 'divider',
                      bgcolor: releaseForm.isMinRequired
                        ? (t) => alpha(t.palette.warning.main, 0.08)
                        : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Min / Force</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mandatory update floor
                          </Typography>
                        </Box>
                        <Switch
                          checked={!!releaseForm.isMinRequired}
                          onChange={(e) => setReleaseForm((p) => ({ ...p, isMinRequired: e.target.checked }))}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {(releaseForm.isLatest || releaseForm.isMinRequired) && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Saving will sync gate settings for{' '}
                  <strong>{APP_LABEL[releaseForm.app]}</strong>{' '}
                  {releaseForm.platform === 'ios' ? 'iOS' : 'Android'}.
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeReleaseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={
              saveReleaseMut.isPending
              || !releaseForm.version.trim()
              || !isSemverLike(releaseForm.version)
              || !releaseForm.storeUrl.trim()
            }
            onClick={() => saveReleaseMut.mutate()}
          >
            {saveReleaseMut.isPending ? 'Saving…' : editingId ? 'Update version' : 'Add version'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete store version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This removes the row from the version master. Mobile gate settings are not deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMut.isPending || !deleteId}
            onClick={() => deleteId && deleteMut.mutate(deleteId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!flash} autoHideDuration={4000} onClose={() => setFlash(null)}>
        {flash ? <Alert severity={flash.severity} onClose={() => setFlash(null)}>{flash.message}</Alert> : undefined}
      </Snackbar>
    </PageShell>
  );
}
